import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { User, Save, LogOut, Trash2, Sparkles } from "lucide-react";

const SENIORITY = ["Entry", "Junior", "Mid", "Senior", "Lead", "Principal"];

export default function Profile() {
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [newSkill, setNewSkill] = useState("");

  useEffect(() => {
    if (user?.profile) setProfile({ ...user.profile });
    else setProfile({ name: user?.name || "", primary_role: "", location: "", seniority_level: "Mid", years_experience: 0, summary: "", skills: [] });
  }, [user]);

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      await api.patch("/profile", profile);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) { alert(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const addSkill = () => {
    const s = newSkill.trim();
    if (!s) return;
    setProfile({ ...profile, skills: [...(profile.skills || []), s] });
    setNewSkill("");
  };

  const removeSkill = (idx) => {
    setProfile({ ...profile, skills: profile.skills.filter((_, i) => i !== idx) });
  };

  const deleteAll = async () => {
    if (!window.confirm("This will permanently delete your account and ALL your data (resumes, jobs, applications, connections, interview sessions). Continue?")) return;
    if (!window.confirm("Are you 100% sure? This cannot be undone.")) return;
    try {
      await api.delete("/user/me?confirm=true");
      alert("All your data has been permanently deleted.");
      logout();
    } catch (e) { alert("Deletion failed. Try again."); }
  };

  if (!profile) return <div className="text-zinc-400">Loading…</div>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="profile-page">
      <div className="jr-card flex items-center gap-4">
        {user?.picture ? <img src={user.picture} alt="" className="w-16 h-16 rounded-full" /> : (
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-500 to-cyan-400 grid place-items-center text-white font-bold text-xl">
            {(user?.name || "U").slice(0, 2).toUpperCase()}
          </div>
        )}
        <div className="flex-1">
          <div className="text-xl font-bold text-white">{user?.name}</div>
          <div className="text-sm text-zinc-400">{user?.email}</div>
          <div className="text-xs text-zinc-500 mt-1">Plan: <span className="text-violet-300">{user?.plan || "Free"}</span></div>
        </div>
        <button onClick={logout} className="jr-btn-ghost text-sm" data-testid="logout-btn">
          <LogOut className="w-4 h-4 inline mr-1" /> Sign out
        </button>
      </div>

      <div className="jr-card space-y-4" data-testid="profile-form">
        <div className="flex items-center gap-2 mb-2">
          <User className="w-5 h-5 text-violet-300" />
          <div className="font-semibold text-white">Career Profile</div>
          <span className="text-xs text-zinc-500">Auto-filled by CV Parser Agent — edit anytime</span>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Display name" value={profile.name || ""} onChange={(v) => setProfile({ ...profile, name: v })} testid="field-name" />
          <Field label="Primary role" value={profile.primary_role || ""} onChange={(v) => setProfile({ ...profile, primary_role: v })} placeholder="e.g. Gen AI Engineer" testid="field-role" />
          <Field label="Location" value={profile.location || ""} onChange={(v) => setProfile({ ...profile, location: v })} placeholder="e.g. Pune, India" testid="field-location" />
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Seniority</label>
            <select value={profile.seniority_level || "Mid"} onChange={(e) => setProfile({ ...profile, seniority_level: e.target.value })} className="jr-input" data-testid="field-seniority">
              {SENIORITY.map(s => <option key={s} className="bg-[#161635]">{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Years of experience</label>
            <input type="number" min="0" max="50" value={profile.years_experience ?? 0} onChange={(e) => setProfile({ ...profile, years_experience: parseInt(e.target.value) || 0 })} className="jr-input" data-testid="field-years" />
          </div>
        </div>

        <div>
          <label className="text-xs text-zinc-400 mb-1 block">Summary</label>
          <textarea rows={4} value={profile.summary || ""} onChange={(e) => setProfile({ ...profile, summary: e.target.value })} className="jr-input" placeholder="Short pitch about you (used by AI agents to match jobs and tailor resumes)" data-testid="field-summary" />
        </div>

        <div>
          <label className="text-xs text-zinc-400 mb-2 block">Skills ({(profile.skills || []).length})</label>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {(profile.skills || []).map((s, i) => (
              <span key={`${s}-${i}`} className="jr-pill bg-violet-500/15 text-violet-100 flex items-center gap-1.5">
                {s}
                <button onClick={() => removeSkill(i)} className="text-violet-300 hover:text-rose-300">×</button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input value={newSkill} onChange={(e) => setNewSkill(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSkill(); } }}
              placeholder="Add a skill (e.g. LangChain, RAG, Bedrock)" className="jr-input" data-testid="add-skill-input" />
            <button onClick={addSkill} className="jr-btn-ghost" data-testid="add-skill-btn">Add</button>
          </div>
        </div>

        <div className="flex justify-between items-center pt-3 border-t border-white/5">
          <div className="text-xs">{saved && <span className="text-emerald-300"><Sparkles className="w-3 h-3 inline" /> Saved</span>}</div>
          <button onClick={save} disabled={saving} className="jr-btn-primary" data-testid="save-profile-btn">
            <Save className="w-4 h-4 inline mr-1" /> {saving ? "Saving..." : "Save Profile"}
          </button>
        </div>
      </div>

      <div className="jr-card border border-rose-500/20">
        <div className="flex items-center gap-2 mb-2">
          <Trash2 className="w-5 h-5 text-rose-300" />
          <div className="font-semibold text-rose-200">Danger Zone</div>
        </div>
        <p className="text-sm text-zinc-400 mb-3">
          Permanently delete your Curcompass account and all associated data (GDPR compliant). Cannot be undone.
        </p>
        <button onClick={deleteAll} className="text-sm text-rose-300 border border-rose-500/40 rounded-lg px-4 py-2 hover:bg-rose-500/10" data-testid="delete-account-btn">
          Delete my account
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder = "", testid }) {
  return (
    <div>
      <label className="text-xs text-zinc-400 mb-1 block">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="jr-input" data-testid={testid} />
    </div>
  );
}
